import { useLocalSearchParams } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'

import usePlatformColor from '@/lib/hooks/usePlatformColor'
import FormOverview from '@/modules/formOverview/FormOverview'
import { fetchFormStatsThunk, fetchFormThunk } from '@/redux/slices/formSlice'
import { fetchFormSettingThunk } from '@/redux/slices/formSettingSlice'
import { closePublishSheet } from '@/redux/slices/publishSheetSlice'
import {
  selectUserFormSettingByFormToken,
  updateUserFormSettingFavoredThunk,
} from '@/redux/slices/userFormSettingSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { Platform, RefreshControl } from 'react-native'
import Animated, { useSharedValue, useAnimatedScrollHandler } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import FormStackScreen from './FormStackScreen'
import { isBelowiOS26 } from '@/lib/utils/iosCompat'

export default function FormOverviewPage() {
  const dispatch = useAppDispatch()
  const { formToken } = useLocalSearchParams<{ formToken: string }>()
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    return () => {
      dispatch(closePublishSheet())
    }
  }, [dispatch])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await Promise.all([
      dispatch(fetchFormThunk({ formToken })),
      dispatch(fetchFormSettingThunk({ formToken })),
      dispatch(fetchFormStatsThunk({ formToken })),
    ])
    setRefreshing(false)
  }, [dispatch, formToken])

  const userFormSetting = useAppSelector(state => selectUserFormSettingByFormToken(state, formToken))

  const handleFavoriteButtonPress = () => {
    dispatch(updateUserFormSettingFavoredThunk({ token: formToken, favored: !userFormSetting?.favored }))
  }

  const [systemGroupedBackground] = usePlatformColor('systemGroupedBackgroundColor')

  // iOS 26+ floats a liquid-glass header over the cover image; other platforms use the
  // stack's default opaque header so the previous screen doesn't bleed through during push.
  const hasLiquidGlassHeader = Platform.OS === 'ios' && !isBelowiOS26

  const insets = useSafeAreaInsets()
  const scrollY = useSharedValue(0)
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y
    },
  })

  return (
    <Animated.ScrollView
      showsVerticalScrollIndicator={false}
      style={{ backgroundColor: systemGroupedBackground }}
      contentContainerStyle={Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined}
      onScroll={scrollHandler}
      scrollEventThrottle={16}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
    >
      <FormStackScreen
        formToken={formToken}
        defaultBackHref="/forms"
        hideTitle={true}
        headerTransparent={hasLiquidGlassHeader}
        headerStyle={hasLiquidGlassHeader ? { backgroundColor: 'transparent' } : undefined}
        headerButtonClassName={hasLiquidGlassHeader ? 'bg-white/60 dark:bg-black/30' : undefined}
        headerRightItems={() => [
          {
            type: 'button',
            label: '收藏',
            icon: {
              name: userFormSetting?.favored ? 'star.fill' : 'star',
              type: 'sfSymbol',
            },
            onPress: handleFavoriteButtonPress,
          },
        ]}
      />
      <FormOverview formToken={formToken} scrollY={scrollY} />
    </Animated.ScrollView>
  )
}
